import { Link } from 'react-router-dom';
import ModuleDemo from '../components/ModuleDemos';
import { MODULES } from '../data/modules';
import { appKit, dollars, platformFree, platformStart } from '../data/plans';
import WHATS_NEW, { WHATS_NEW_LEAD } from '../data/whatsNew';
import WhatsNewArt from '../components/WhatsNewArt';
import PageWorld from '../components/site/PageWorld';
import ProductHandoff from '../components/site/ProductHandoff';
import { PLATFORM } from '../data/worlds';
import { INVOICE } from '../data/spine';
import { PageFilm } from '../components/PageFilm';
import PitchButton from '../components/PitchButton';

// /platform — the full module catalog with live demo widgets. The homepage
// tells one buyer one story (the invoice, with a person in the loop); this
// page continues that sentence — the same invoice read back by module —
// and then relaxes the "name two things" rule so the whole stack gets its
// cards. Each card links to its detailed walkthrough (/platform/:slug).
// Module content lives in data/modules.js; the invoice steps and the App
// Kit hand-off in data/spine.js (shared with the homepage and /app-kit).

export default function Platform() {
  return (
    <PageWorld world={PLATFORM}>
    <div className="cs-store cs-land">
      <header className="cs-store-hero">
        <span className="cs-mod-kicker">Platform · {platformStart.price}</span>
        <h1>Design the workflow in nQube. agentOS runs it, behind a gate.</h1>
        <p>
          On the homepage, Finance posted an invoice with a person in the
          loop. This page is each thing that did a part of it. Demos use
          synthetic data.
        </p>
        <div className="cs-land-cta-row cs-land-cta-center">
          <Link to={platformStart.href} className="cs-btn cs-btn-primary">{platformStart.cta}</Link>
          <Link to={platformFree.href} className="cs-btn cs-btn-outline">{platformFree.cta}</Link>
        </div>
        <p className="cs-land-micro" style={{ textAlign: 'center' }}>
          <Link to="/#proof">See the invoice run →</Link>
        </p>
      </header>

      <PageFilm
        base="/media/platform-film"
        prefix="platform"
        title="Platform — the canvas is the specification"
        summary="Draw the work once: branches are ports, loops end, steps call pipelines or themselves. The gate holds; NoVA reads your screen and leaves the guarded buttons to you; the brain shows how it knew. One measured number."
        chapters={[
          { at: 0, title: 'Draw it' },
          { at: 32.08, title: 'The gate' },
          { at: 57.25, title: 'NoVA' },
          { at: 73.58, title: 'The brain' },
          { at: 83.5, title: 'Measured' },
        ]}
      />
      <PitchButton focus="platform" hint="Or hear it from NoVA and interrupt with questions." />

      <section className="cs-store-section" id="invoice">
        <h2>The invoice, by module</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          The four screens the homepage replays, and the page that owns each one.
        </p>
        <ol className="cs-plat-steps">
          {INVOICE.map((s) => (
            <li key={s.n}>
              <span>{s.n}</span>
              <strong>{s.k}</strong>
              <p>{s.what}</p>
              <Link to={s.moduleTo || s.to} className="cs-land-proof-link">{s.module} →</Link>
            </li>
          ))}
        </ol>
      </section>

      <section className="cs-store-section" id="design">
        <h2>Design and runtime</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          nQube is where a workflow is designed, and it comes with Platform.
          agentOS commits that workflow only after a gate, and only inside a
          capability you declared.
        </p>
        <div className="cs-land-modules">
          {['nqube', 'agentos'].map((slug) => MODULES.find((m) => m.slug === slug)).map((m) => (
            <div key={m.slug} className="cs-sw-card cs-land-mod">
              <ModuleDemo name={m.demo} />
              <h3>{m.name}</h3>
              <p>{m.card}</p>
              <Link to={`/platform/${m.slug}`} className="cs-land-textlink">
                Walkthrough →
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="cs-store-section" id="agents">
        <h2>Agents</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          Three names get mixed together — Agent IO, nventr Agent, agentOS —
          and they are three different things. agentOS is the runtime above.
          Agent IO is where you define the agent: voice, what it may read
          and propose, and how the launcher looks. nventr Agent is that
          definition on a page you already have — a script, an iframe, or
          the API. A coordinator block can run several Agent IO roles. It
          still cannot commit.
        </p>
        <div className="cs-land-modules">
          {['agent-io', 'nventr-agent'].map((slug) => MODULES.find((m) => m.slug === slug)).map((m) => (
            <div key={m.slug} className="cs-sw-card cs-land-mod">
              <ModuleDemo name={m.demo} />
              <h3>{m.name}</h3>
              <p>{m.card}</p>
              <Link to={`/platform/${m.slug}`} className="cs-land-textlink">
                Walkthrough →
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="cs-store-section" id="modules">
        <h2>Capabilities</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          What the runtime did, where data comes in, who gets told, and
          deploys, health and logs.
        </p>
        <div className="cs-land-modules">
          {MODULES.filter((m) => m.kind !== 'name' && m.slug !== 'nqube' && m.slug !== 'dev-console').map((m) => (
            <div key={m.slug} className="cs-sw-card cs-land-mod">
              <ModuleDemo name={m.demo} />
              <h3>{m.name}</h3>
              <p>{m.card}</p>
              <Link to={`/platform/${m.slug}`} className="cs-land-textlink">
                Walkthrough →
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="cs-store-section" id="app-kit">
        <h2>Need the app, not just the workflow? That is App Kit.</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          The second product: your URL, database and layer repo, Dev Console
          included. Dev Console builds and deploys that app from <code>/dev</code>{' '}
          on the instance; it comes with App Kit, not with Platform at {dollars(platformStart)}.
        </p>
        <div className="cs-land-modules">
          {MODULES.filter((m) => m.slug === 'dev-console').map((m) => (
            <div key={m.slug} className="cs-sw-card cs-land-mod">
              <ModuleDemo name={m.demo} />
              <span className="cs-mod-kicker">Requires App Kit</span>
              <h3>{m.name}</h3>
              <p>{m.card}</p>
              <Link to={`/platform/${m.slug}`} className="cs-land-textlink">
                Walkthrough →
              </Link>
            </div>
          ))}
        </div>
        <ProductHandoff here="platform">
          <Link to="/app-kit" className="cs-btn cs-btn-outline">How App Kit works →</Link>
          <Link to={appKit.href} className="cs-btn cs-btn-outline">Launch App Kit · {dollars(appKit)}</Link>
        </ProductHandoff>
      </section>

      <section className="cs-store-section" id="built-on">
        <h2>A workflow on Platform</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          Invoice intake is a gated pipeline into systems you already use.
          An app people log into is App Kit, not a module.
        </p>
        <div className="cs-sw-card cs-land-builton">
          <ModuleDemo name="IDP" />
          <div className="cs-land-builton-copy">
            <span className="cs-mod-kicker">Solution</span>
            <h3>IDP — Intelligent Document Processing</h3>
            <p>
              Pretrained neural-network workflows for high-accuracy document
              extraction at scale: invoices, claims, contracts. Extraction
              agents propose, gates hold anything above policy thresholds,
              and only the runtime posts to your systems of record.
            </p>
            <div className="cs-land-cta-row">
              <Link
                to="/contact?subject=IDP"
                className="cs-btn cs-btn-outline"
              >
                Bring IDP to your documents
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Moved here from the homepage 2026-10-14 ([[homepage-conversion-first]]).
          Recut 2026-10-22: the lead notes are the last ten days, written out. */}
      <section className="cs-store-section" id="whats-new">
        <h2>What&rsquo;s new</h2>
        <p className="cs-store-note cs-land-new-intro">
          October 15–22, written out. Under them, the rest of the month — still current, in shorter form.
        </p>
        <ul className="cs-land-new-deep">
          {WHATS_NEW_LEAD.map((n) => {
            const inner = (
              <>
                <div className="cs-land-new-art"><WhatsNewArt kind={n.art} /></div>
                <div className="cs-land-new-copy">
                  <time dateTime={n.date}>{n.date}</time>
                  <div className="cs-land-new-place">{n.place}</div>
                  <h3>{n.title}</h3>
                  {n.body.map((p) => <p key={p}>{p}</p>)}
                </div>
              </>
            );
            return (
              <li key={n.title} id={`new-${n.art}`}>
                {n.to
                  ? <Link to={n.to} className="cs-land-new-feature">{inner}</Link>
                  : <div className="cs-land-new-feature">{inner}</div>}
              </li>
            );
          })}
        </ul>
        <h3 className="cs-land-new-earlier">Also this month</h3>
        <ul className="cs-land-new">
          {WHATS_NEW.map((n) => (
            <li key={n.title}>
              <Link to={n.to} className="cs-land-new-card">
                <div className="cs-land-new-art"><WhatsNewArt kind={n.art} /></div>
                <time dateTime={n.date}>{n.date}</time>
                <strong>{n.title}</strong>
                <span>{n.body}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="cs-land-band">
        <h2>Start on the systems you already use</h2>
        <p>
          Start is {platformStart.helper}. The free canvas has no production
          runs. Standing up the first pipeline with us is a separate delivery.
        </p>
        <div className="cs-land-cta-row cs-land-cta-center">
          <Link to={platformStart.href} className="cs-btn cs-btn-primary">
            {platformStart.cta}
          </Link>
          <Link to={platformFree.href} className="cs-btn cs-btn-outline">
            {platformFree.cta}
          </Link>
          <Link to="/app-kit" className="cs-btn cs-btn-outline">
            Rent App Kit
          </Link>
        </div>
      </section>
    </div>
    </PageWorld>
  );
}
